/**
 * Port of `Vacancy.RenderPagination`, which the vacancy listing, Saved Vacancy
 * and Application History each reimplemented identically.
 *
 * Behaviour is preserved, including its quirks: every page number is listed with
 * no windowing (60 pages means 60 links), and Next is disabled when there is
 * only one page. Total pages = ceil(Count / PageSize).
 */
interface PaginationProps {
  /** `Count` from the PagedResponse — total rows, not pages. */
  count: number
  pageNumber: number
  pageSize: number
  onPageChange: (page: number) => void
}

export function Pagination({
  count,
  pageNumber,
  pageSize,
  onPageChange,
}: PaginationProps): JSX.Element | null {
  const totalPages = Math.ceil(count / pageSize)
  if (!Number.isFinite(totalPages) || totalPages < 1) return null

  const isFirst = pageNumber === 1
  // Reproduces the original condition: Next is also disabled on a single page.
  const isLast = pageNumber === totalPages || totalPages === 1

  return (
    <nav aria-label="Page navigation">
      <ul className="pagination justify-content-center" id="paginationControls">
        <li className={`page-item${isFirst ? ' disabled' : ''}`}>
          <a
            className="page-link"
            href="#!"
            aria-label="Previous"
            onClick={(event) => {
              event.preventDefault()
              if (!isFirst) onPageChange(pageNumber - 1)
            }}
          >
            <span aria-hidden="true">«</span>
            <span className="sr-only">Previous</span>
          </a>
        </li>

        {Array.from({ length: totalPages }, (_, index) => index + 1).map((page) => (
          <li key={page} className={`page-item${page === pageNumber ? ' active' : ''}`}>
            <a
              className="page-link"
              href="#!"
              onClick={(event) => {
                event.preventDefault()
                onPageChange(page)
              }}
            >
              {page}
            </a>
          </li>
        ))}

        <li className={`page-item${isLast ? ' disabled' : ''}`}>
          <a
            className="page-link"
            href="#!"
            aria-label="Next"
            onClick={(event) => {
              event.preventDefault()
              if (!isLast) onPageChange(pageNumber + 1)
            }}
          >
            <span aria-hidden="true">»</span>
            <span className="sr-only">Next</span>
          </a>
        </li>
      </ul>
    </nav>
  )
}
